import {
  BusinessGetResponseType,
  BusinessListParametersType,
  BusinessListResponseType,
} from "@sellar-io/api-v2/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  ApiV2InfiniteQueryOptions,
  apiV2Request,
  getDefaultInfiniteQueryOptions,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";
import type { ExtendedFirebaseUser } from "../../../useFirebaseUser.shared";

export const fetchBusinessListPage = <TData = BusinessListResponseType,>({
  user,
  business,
  params,
  offset = 0,
}: {
  user: ExtendedFirebaseUser | undefined;
  business: BusinessGetResponseType | null;
  params: BusinessListParametersType;
  offset?: number;
}): Promise<TData> =>
  apiV2Request({
    user,
    business,
    method: "get",
    path: "businesses",
    params: { ...params, offset },
  }).then((response) => apiV2AssertResponse<TData>(response).data);

export const useBusinessList = <TData = BusinessListResponseType,>(
  params: BusinessListParametersType,
  options?: ApiV2InfiniteQueryOptions<TData>,
) => {
  const currentBusinessContext = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useInfiniteQuery({
    queryKey: [
      user?.uid,
      currentBusinessContext?.business?.id,
      ServerStateKeys.Businesses,
      params,
    ],
    queryFn: (queryParams) =>
      fetchBusinessListPage<TData>({
        user,
        business: currentBusinessContext?.business ?? null,
        params,
        offset: queryParams.pageParam,
      }),
    ...getDefaultInfiniteQueryOptions({ ...options, limit: params.limit }),
  });
};
